/**
 * PayByPrepFeesCard - Displays bank approval status and EFIN/bank management
 * for users on the Pay by Prep Fees (pay_by_refund) payment method.
 */

import { useState, useEffect } from "react";
import {
  Alert,
  Badge,
  Button,
  Divider,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconBuildingBank,
  IconCheck,
  IconClock,
  IconX,
  IconAlertCircle,
  IconAlertTriangle,
  IconEdit,
  IconInfoCircle,
} from "@tabler/icons-react";
import type { BankApprovalStatus, PayByPrepFeesInfo } from "../types";
import { BANK_PRODUCT_OPTIONS, SUPPORTED_PAY_BY_REFUND_BANKS } from "../types";

interface PayByPrepFeesCardProps {
  /** Bank approval and EFIN information */
  prepFeesInfo: PayByPrepFeesInfo | null;
  /** Whether the data is currently loading */
  isLoading?: boolean;
  /** Callback when user updates their EFIN */
  onUpdateEfin?: (efin: string) => Promise<void>;
  /** Callback when user updates their bank product */
  onUpdateBank?: (bankProduct: string) => Promise<void>;
}

/**
 * Get badge configuration based on approval status.
 *
 * @param status - The bank approval status
 * @returns Badge color, icon, and label for the status
 */
const getApprovalStatusBadge = (status: BankApprovalStatus | null) => {
  switch (status) {
    case "approved":
      return {
        color: "green",
        icon: <IconCheck size={14} />,
        label: "Approved",
      };
    case "pending":
      return {
        color: "yellow",
        icon: <IconClock size={14} />,
        label: "Pending",
      };
    case "needs_action":
      return {
        color: "yellow",
        icon: <IconAlertTriangle size={14} />,
        label: "Needs Action",
      };
    case "denied":
      return {
        color: "red",
        icon: <IconX size={14} />,
        label: "Denied",
      };
    case "not_supported":
      return {
        color: "orange",
        icon: <IconAlertCircle size={14} />,
        label: "Not Supported",
      };
    case "no_bank_product":
      return {
        color: "gray",
        icon: <IconAlertCircle size={14} />,
        label: "No Bank Selected",
      };
    default:
      return {
        color: "gray",
        icon: <IconAlertCircle size={14} />,
        label: "Unknown",
      };
  }
};

/**
 * Get status-specific messaging for the user.
 *
 * @param status - The bank approval status
 * @param bankName - The name of the bank product
 * @param approvalMessage - Optional message from the backend
 * @returns User-facing message about their approval status
 */
const getStatusMessage = (
  status: BankApprovalStatus | null,
  bankName: string | null,
  approvalMessage?: string | null
) => {
  // Use backend message if provided
  if (approvalMessage) {
    return approvalMessage;
  }

  const bank = bankName || "your bank";
  switch (status) {
    case "approved":
      return `You're approved by ${bank} to have SmartWiz fees deducted from client refunds.`;
    case "pending":
      return `Your approval request with ${bank} is being processed. You'll be notified when it's complete.`;
    case "needs_action":
      return `Action required: Your bank approval needs attention. Please update your EFIN or bank information to continue using Pay by Prep Fees.`;
    case "denied":
      return `Your approval request was denied by ${bank}. Your card on file will be charged for usage.`;
    case "not_supported":
      return `${bank} is not supported for Pay by Prep Fees. Your card on file will be charged for SmartWiz usage.`;
    case "no_bank_product":
      return `No bank product selected. Please select a bank to enable Pay by Prep Fees.`;
    default:
      return `Unable to determine your approval status with ${bank}.`;
  }
};

/**
 * Check if a bank product is supported for Pay by Prep Fees.
 */
const isBankSupported = (bankProduct: string | null): boolean => {
  if (!bankProduct) return false;
  return SUPPORTED_PAY_BY_REFUND_BANKS.includes(
    bankProduct as (typeof SUPPORTED_PAY_BY_REFUND_BANKS)[number]
  );
};

export const PayByPrepFeesCard = ({
  prepFeesInfo,
  isLoading = false,
  onUpdateEfin,
  onUpdateBank,
}: PayByPrepFeesCardProps) => {
  // EFIN editing state
  const [isEditingEfin, setIsEditingEfin] = useState(false);
  const [editedEfin, setEditedEfin] = useState(prepFeesInfo?.efin || "");
  const [isSavingEfin, setIsSavingEfin] = useState(false);
  const [efinError, setEfinError] = useState<string | null>(null);

  // Bank editing state
  const [isEditingBank, setIsEditingBank] = useState(false);
  const [editedBank, setEditedBank] = useState(prepFeesInfo?.bankProduct || "");
  const [isSavingBank, setIsSavingBank] = useState(false);
  const [bankError, setBankError] = useState<string | null>(null);

  // Sync local state when prepFeesInfo changes
  useEffect(() => {
    if (prepFeesInfo) {
      setEditedEfin(prepFeesInfo.efin || "");
      setEditedBank(prepFeesInfo.bankProduct || "");
    }
  }, [prepFeesInfo]);

  /**
   * Handle saving the updated EFIN.
   * Validates format and calls the update callback.
   */
  const handleSaveEfin = async () => {
    if (!onUpdateEfin) return;

    // Validate EFIN format (6 digits)
    if (!/^\d{6}$/.test(editedEfin)) {
      setEfinError("EFIN must be exactly 6 digits");
      return;
    }

    setIsSavingEfin(true);
    setEfinError(null);

    try {
      await onUpdateEfin(editedEfin);
      setIsEditingEfin(false);
    } catch (error) {
      setEfinError("Failed to update EFIN. Please try again.");
    } finally {
      setIsSavingEfin(false);
    }
  };

  /**
   * Handle canceling the EFIN edit.
   * Resets the input to the original value.
   */
  const handleCancelEfinEdit = () => {
    setEditedEfin(prepFeesInfo?.efin || "");
    setIsEditingEfin(false);
    setEfinError(null);
  };

  /**
   * Handle saving the updated bank product.
   */
  const handleSaveBank = async () => {
    if (!onUpdateBank || !editedBank) return;

    setIsSavingBank(true);
    setBankError(null);

    try {
      await onUpdateBank(editedBank);
      setIsEditingBank(false);
    } catch (error) {
      setBankError("Failed to update bank. Please try again.");
    } finally {
      setIsSavingBank(false);
    }
  };

  /**
   * Handle canceling the bank edit.
   * Resets the selection to the original value.
   */
  const handleCancelBankEdit = () => {
    setEditedBank(prepFeesInfo?.bankProduct || "");
    setIsEditingBank(false);
    setBankError(null);
  };

  // Loading state
  if (isLoading) {
    return (
      <Paper withBorder radius="md" p="xl" maw={1000}>
        <Group justify="center" p="md">
          <Loader size="sm" />
          <Text c="dimmed">Loading payment information...</Text>
        </Group>
      </Paper>
    );
  }

  // No data state
  if (!prepFeesInfo) {
    return (
      <Paper withBorder radius="md" p="xl" maw={1000}>
        <Text c="dimmed">Unable to load payment information.</Text>
      </Paper>
    );
  }

  const { bankProduct, approvalStatus, efin, approvalMessage } = prepFeesInfo;
  const bankSupported = isBankSupported(bankProduct);
  const statusBadge = getApprovalStatusBadge(approvalStatus);
  const statusMessage = getStatusMessage(
    approvalStatus,
    bankProduct,
    approvalMessage
  );

  // Check if selected bank in edit mode is supported
  const editedBankSupported = isBankSupported(editedBank);

  return (
    <Paper withBorder radius="md" p="xl" maw={1000}>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="xs">
            <ThemeIcon variant="light" size="lg" radius="md" color="blue">
              <IconBuildingBank size={20} />
            </ThemeIcon>
            <div>
              <Text size="lg" fw={600}>
                Pay by Prep Fees
              </Text>
              <Text size="sm" c="dimmed">
                SmartWiz fees are deducted from client refunds
              </Text>
            </div>
          </Group>

          {/* Approval Status Badge */}
          <Badge
            size="lg"
            variant="light"
            color={statusBadge.color}
            leftSection={statusBadge.icon}
          >
            {statusBadge.label}
          </Badge>
        </Group>

        <Divider />

        {/* Bank Information */}
        <Group gap="xl" wrap="wrap">
          {/* Bank Product Section */}
          <Stack gap={4}>
            <Text size="sm" c="dimmed" fw={500}>
              Bank Product
            </Text>
            {isEditingBank ? (
              <Stack gap="xs">
                <Group gap="xs">
                  <Select
                    value={editedBank}
                    onChange={(value) => setEditedBank(value || "")}
                    data={BANK_PRODUCT_OPTIONS.map((opt) => ({
                      value: opt.id,
                      label: opt.name,
                    }))}
                    placeholder="Select a bank"
                    size="sm"
                    w={200}
                    error={bankError}
                    disabled={isSavingBank}
                  />
                  <Button
                    size="xs"
                    onClick={handleSaveBank}
                    loading={isSavingBank}
                    disabled={!editedBank}
                  >
                    Save
                  </Button>
                  <Button
                    size="xs"
                    variant="subtle"
                    onClick={handleCancelBankEdit}
                    disabled={isSavingBank}
                  >
                    Cancel
                  </Button>
                </Group>
                {editedBank && editedBank !== bankProduct && (
                  <Alert
                    icon={<IconAlertCircle size={14} />}
                    color="orange"
                    variant="light"
                    py="xs"
                  >
                    <Text size="xs">
                      Changing your bank will reset your approval status to
                      pending.
                      {!editedBankSupported && (
                        <Text span fw={600}>
                          {" "}
                          Note: This bank is not supported for Pay by Prep Fees.
                        </Text>
                      )}
                    </Text>
                  </Alert>
                )}
              </Stack>
            ) : (
              <Group gap="xs">
                <Text fw={600}>{bankProduct || "Not specified"}</Text>
                {bankProduct && (
                  <Tooltip
                    label={
                      bankSupported
                        ? "This bank supports Pay by Prep Fees"
                        : `This bank is not currently supported. Supported banks: ${SUPPORTED_PAY_BY_REFUND_BANKS.join(", ")}`
                    }
                    multiline
                    w={250}
                  >
                    <Badge
                      size="sm"
                      variant="dot"
                      color={bankSupported ? "green" : "orange"}
                    >
                      {bankSupported ? "Supported" : "Not Supported"}
                    </Badge>
                  </Tooltip>
                )}
                {onUpdateBank && (
                  <Tooltip label="Change bank">
                    <Button
                      variant="subtle"
                      size="xs"
                      p={4}
                      onClick={() => setIsEditingBank(true)}
                    >
                      <IconEdit size={14} />
                    </Button>
                  </Tooltip>
                )}
              </Group>
            )}
          </Stack>

          {/* EFIN Section */}
          <Stack gap={4}>
            <Text size="sm" c="dimmed" fw={500}>
              EFIN
            </Text>
            {isEditingEfin ? (
              <Stack gap="xs">
                <Group gap="xs">
                  <TextInput
                    value={editedEfin}
                    onChange={(e) => setEditedEfin(e.target.value)}
                    placeholder="XXXXXX"
                    maxLength={6}
                    size="sm"
                    w={120}
                    error={efinError}
                    disabled={isSavingEfin}
                  />
                  <Button
                    size="xs"
                    onClick={handleSaveEfin}
                    loading={isSavingEfin}
                    disabled={!editedEfin}
                  >
                    Save
                  </Button>
                  <Button
                    size="xs"
                    variant="subtle"
                    onClick={handleCancelEfinEdit}
                    disabled={isSavingEfin}
                  >
                    Cancel
                  </Button>
                </Group>
                {editedEfin && editedEfin !== efin && (
                  <Alert
                    icon={<IconAlertCircle size={14} />}
                    color="orange"
                    variant="light"
                    py="xs"
                  >
                    <Text size="xs">
                      Changing your EFIN will reset your approval status to
                      pending.
                    </Text>
                  </Alert>
                )}
              </Stack>
            ) : (
              <Group gap="xs">
                <Text fw={600} ff="monospace">
                  {efin || "Not set"}
                </Text>
                {onUpdateEfin && (
                  <Tooltip label="Update EFIN">
                    <Button
                      variant="subtle"
                      size="xs"
                      p={4}
                      onClick={() => setIsEditingEfin(true)}
                    >
                      <IconEdit size={14} />
                    </Button>
                  </Tooltip>
                )}
              </Group>
            )}
          </Stack>
        </Group>

        {/* Status Message */}
        <Alert
          icon={<IconInfoCircle size={16} />}
          color={
            approvalStatus === "approved"
              ? "green"
              : approvalStatus === "denied"
                ? "red"
                : approvalStatus === "needs_action"
                  ? "yellow"
                  : approvalStatus === "not_supported"
                    ? "orange"
                    : "blue"
          }
          variant="light"
        >
          <Text size="sm">{statusMessage}</Text>
          {approvalStatus === "denied" && (
            <Text size="sm" mt="xs" c="dimmed">
              Contact support if you believe this is an error.
            </Text>
          )}
          {approvalStatus === "needs_action" && (
            <Text size="sm" mt="xs" c="dimmed">
              Please update your information above to resolve this issue.
            </Text>
          )}
        </Alert>

        {/* Unsupported Bank Warning - only show if status isn't already not_supported */}
        {bankProduct &&
          !bankSupported &&
          approvalStatus !== "not_supported" && (
            <Alert icon={<IconAlertCircle size={16} />} color="orange">
              <Text size="sm">
                Your bank ({bankProduct}) is not currently supported for Pay by
                Prep Fees. Your card on file will be charged for SmartWiz usage.
              </Text>
              <Text size="sm" mt="xs" c="dimmed">
                Supported banks: {SUPPORTED_PAY_BY_REFUND_BANKS.join(", ")}
              </Text>
            </Alert>
          )}
      </Stack>
    </Paper>
  );
};
